/**
 * PRODUCT_DEV-641 テナントのログイン画面ブランド — 文言の規約（純関数）。
 *
 * ロゴの S3 キー規約は `lib/tenantLogo.ts`。こちらは**文字列の扱い**だけを持つ。
 *
 * ## なぜ共通化するか
 *
 * 同じ正規化を**書く側**（Unizo のテナント設定。PRODUCT_DEV-889）と**読む側**（未認証の
 * tenant-branding API）の両方が要る。片方だけ直すと「保存はできるのに画面に出ない」
 * 「消したのに残る」が起きる。
 */

/**
 * 制御文字と書式文字（向きを変える U+202E、ゼロ幅文字など）。
 * 呼び方は未認証のログイン画面に出るので、見た目と中身が食い違う文字は保存させない。
 */
const CONTROL_OR_FORMAT = /[\p{Cc}\p{Cf}]/u;

/** 呼び方の長さ上限。入力欄のラベルに収まる範囲。 */
export const LOGIN_ID_LABEL_MAX = 20;

/**
 * ログイン ID 欄の呼び方を正規化する。**空は null**（＝未設定）。
 *
 * 空文字を「設定済みの空文字」として保存すると、ログイン画面がそれを採用して
 * 欄のラベルが消える。書く側は null を REMOVE に、読む側は null を「未設定」に倒す。
 */
export const normalizeLoginIdLabel = (
  raw: string | null | undefined,
): string | null => normalizeTenantLabel(raw);

/** 上限を超えていないか・制御文字を含まないか。通らないものは保存させない。 */
export const isValidLoginIdLabel = (raw: string | null | undefined): boolean =>
  isValidTenantLabel(raw, LOGIN_ID_LABEL_MAX);

/**
 * テナントごとに変えられる呼び名（ログイン ID 欄・顧客番号など）の正規化。**空は null**。
 * PRODUCT_DEV-1239: 顧客番号の呼び名も同じ決まりで扱うため、名前に依らない形で持つ。
 */
export const normalizeTenantLabel = (
  raw: string | null | undefined,
): string | null => {
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : null;
};

/** 呼び名が上限以内で、制御文字・書式文字を含まないか（空は未設定として通す）。 */
export const isValidTenantLabel = (
  raw: string | null | undefined,
  max: number,
): boolean => {
  const v = normalizeTenantLabel(raw);
  return v === null || (v.length <= max && !CONTROL_OR_FORMAT.test(v));
};
